import type { ApolloError } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TEALIUM_AD_TYPE } from 'src/app/shared/constants/dispatcherId';
import { PAGE_ID_SELECT_AD_TYPE } from 'src/app/shared/constants/pageUid';
import {
  PRODUCT_SET_FAILED_UNAVAILABLE,
  PRODUCT_SET_OK,
  PRODUCT_SET_SELECTED_PRODUCT_PRICE_CHANGED,
  PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE,
} from 'src/app/shared/constants/productSetStatus';
import { delay } from 'src/app/utils/delay';
import { getProductByIdOrFeatures } from 'src/app/utils/getProductByIdOrFeatures';
import { log } from 'src/app/utils/logging';
import tealiumDispatch from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdatePageProgress } from 'src/app/v1/state/modules/app/progress';
import {
  actionResetSelectedProductId,
  actionUpdateSelectedProductId,
} from 'src/app/v1/state/modules/formData/selectedProductId';
import { actionUpdateProductSet } from 'src/app/v1/state/modules/session/productSet';
import { actionUpdateProductSetStatus } from 'src/app/v1/state/modules/session/productSetStatus';
import { actionUpdateProductSetId } from 'src/app/v1/state/modules/tealium/productSetId';
import type { AdProduct, AdProductUpgrade } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { ProductSetFromMutation } from 'src/types/product';

interface UseCreateProductSetInput {
  createProductSetMutation: () => Promise<ProductSetFromMutation | undefined>;
  isInputSame: () => boolean;
  getInputsForLogging: () => Record<string, string>; // currently, all inputs are strings
}

const useCreateProductSet = ({
  createProductSetMutation,
  isInputSame,
  getInputsForLogging,
}: UseCreateProductSetInput) => {
  const {
    productSet: productSetInSession,
    productSetStatus,
    seekAdvertiserId,
    selectedProductId,
    selectedProductFeatures,
    progress,
  } = useSelector(
    (state: AppState) => ({
      seekAdvertiserId: state.session.seekAdvertiserId,
      productSetStatus: state.session.productSetStatus,
      productSet: state.session.productSet,
      selectedProductId: state.formData.selectedProductId,
      selectedProductFeatures: state.session.productFeatures,
      progress: state.app.progress,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const productSetId = productSetInSession?.id;
  const selectedProduct = (
    productSetInSession?.items as Array<AdProduct | AdProductUpgrade>
  )?.find((product) => product?.id === selectedProductId);
  const selectedProductPrice =
    selectedProduct?.price.__typename === 'Price'
      ? selectedProduct?.price?.netPrice?.value
      : undefined;

  const selectedProductPriceRef = useRef<number | undefined | null>(
    selectedProductPrice,
  );
  const selectedProductIdRef = useRef<string | undefined | null>(
    selectedProductId,
  );
  const productSetIdRef = useRef<string | undefined>(productSetId);

  // hook clean up
  useEffect(
    () => () => {
      if (productSetStatus === PRODUCT_SET_SELECTED_PRODUCT_PRICE_CHANGED) {
        dispatch(actionUpdateProductSetStatus(PRODUCT_SET_OK));
      }
    },
    [dispatch, productSetStatus],
  );

  return useCallback(async () => {
    const maxRetries = 2;
    const backoffMs = 1000;
    let doRetry = false;
    let retryCount = 0;
    let createProductSetError;

    do {
      if (retryCount !== 0) {
        await delay(retryCount * backoffMs);
      }

      try {
        retryCount += 1;
        const newProductSet = await createProductSetMutation();

        // do nothing when successful return value is undefined
        if (!newProductSet) {
          return {
            productSet: undefined,
            error: undefined,
          };
        }

        const newProductSetId = newProductSet?.id;

        const newSelectedProduct = getProductByIdOrFeatures({
          productSet: newProductSet,
          selectedProductId: selectedProductIdRef.current ?? undefined,
          selectedProductFeatures,
        });

        const newSelectedProductPrice =
          newSelectedProduct?.price.__typename === 'Price'
            ? newSelectedProduct?.price?.netPrice?.value
            : undefined;
        const newSelectedProductId = newSelectedProduct?.id ?? null;

        const prevProductSetId = productSetIdRef.current;
        const prevSelectedProductId = selectedProductIdRef.current;
        const prevSelectedProductPrice = selectedProductPriceRef.current;

        productSetIdRef.current = newProductSetId;
        selectedProductIdRef.current = newSelectedProductId;
        selectedProductPriceRef.current = newSelectedProductPrice;

        if (newSelectedProductId !== prevSelectedProductId) {
          if (typeof newSelectedProductId === 'string') {
            dispatch(actionUpdateSelectedProductId(newSelectedProductId));
            dispatch(
              tealiumDispatch(TEALIUM_AD_TYPE, newSelectedProduct?.name),
            );
          } else {
            dispatch(actionResetSelectedProductId());
          }
        }

        const newProductSetRecreated = Boolean(
          newProductSetId &&
            prevProductSetId &&
            newProductSetId !== prevProductSetId,
        );
        // REFACTOR: Fix selectedProductId not available in product set
        if (
          newProductSetRecreated &&
          !newSelectedProductId &&
          prevSelectedProductId
        ) {
          dispatch(
            actionUpdateProductSetStatus(
              PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE,
            ),
          );
          log.error(
            'UseCreateProductSet | CreateProductSet | Selected product does not exist in new productSet for advertiser',
            {
              prevSelectedProductId,
              newProductSetId,
              seekAdvertiserId,
              ...getInputsForLogging(),
            },
          );
        } else if (
          isInputSame() &&
          newProductSetRecreated &&
          newSelectedProductId === prevSelectedProductId &&
          newSelectedProductPrice !== prevSelectedProductPrice
        ) {
          // new product set with same job details but different price for same selected product id
          dispatch(
            actionUpdateProductSetStatus(
              PRODUCT_SET_SELECTED_PRODUCT_PRICE_CHANGED,
            ),
          );

          log.error(
            'UseCreateProductSet | CreateProductSet | Selected product has different price in new product set compared to previous product set for advertiser',
            {
              prevSelectedProductId,
              prevProductSetId,
              newProductSetId,
              seekAdvertiserId,
              ...getInputsForLogging(),
            },
          );
        } else if (newProductSetId) {
          dispatch(actionUpdateProductSetStatus(PRODUCT_SET_OK));
        }
        dispatch(actionUpdateProductSet(newProductSet));
        dispatch(actionUpdateProductSetId(newProductSet.id));
        // Ensure progress bar is updated depending on whether
        // a product was selected
        const newSelectAdTypeProgress = newSelectedProductId !== null;
        const existingSelectAdTypeProgress =
          progress.isValidObj[PAGE_ID_SELECT_AD_TYPE];
        if (existingSelectAdTypeProgress !== newSelectAdTypeProgress) {
          dispatch(
            actionUpdatePageProgress(
              PAGE_ID_SELECT_AD_TYPE,
              newSelectAdTypeProgress,
            ),
          );
        }

        return {
          productSet: newProductSet,
          error: undefined,
        };
      } catch (error) {
        createProductSetError = error;
        if (
          (createProductSetError as ApolloError).message ===
          'signal is aborted without reason'
        ) {
          return {
            productSet: undefined,
            error: createProductSetError,
          };
        }

        // we want to retry on advertiser NOT_FOUND errors
        // new hirers might not have data populated in the db yet before creating product set
        if (
          (error as ApolloError).graphQLErrors?.[0]?.extensions?.code ===
          'NOT_FOUND'
        ) {
          doRetry = true;
          log.warn(
            'UseCreateProductSet | CreateProductSet | Create product set failed, attempting retry',
            { retryCount },
          );
        } else {
          doRetry = false;
        }
      }
    } while (doRetry && retryCount <= maxRetries);
    log.error(
      'UseCreateProductSet | CreateProductSet | Create product set failed',
      createProductSetError,
    );
    dispatch(actionUpdateProductSetStatus(PRODUCT_SET_FAILED_UNAVAILABLE));

    return {
      productSet: undefined,
      error: createProductSetError,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    getInputsForLogging,
    isInputSame,
    createProductSetMutation,
    seekAdvertiserId,
  ]);
};

export { useCreateProductSet, type UseCreateProductSetInput };
